import { useState, useEffect, useCallback } from 'react';
import { Drawer, Box, Typography, IconButton, TextField, Button, Alert, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox, Divider } from '@mui/material';
import { X, AlertTriangle, Sparkles } from 'lucide-react';
import { useCreateMetricDefinition, useUpdateMetricDefinition, usePortfolioCompanies } from '@lingaai/shared-hooks';
import { useCurrentUser } from '@lingaai/user-management';
import type { MetricDefinition } from '@lingaai/types';
import { ICON_SIZES, ICON_STROKE } from '../constants/icons';

interface MetricDefinitionDrawerProps {
  open: boolean;
  onClose: () => void;
  definition: MetricDefinition | null;
  fundId: string;
  tenantId: string;
}

export function MetricDefinitionDrawer({ open, onClose, definition, fundId, tenantId }: MetricDefinitionDrawerProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dataType, setDataType] = useState<'number' | 'currency' | 'percentage' | 'text'>('number');
  const [isRequired, setIsRequired] = useState(false);
  const [displayOrder, setDisplayOrder] = useState(0);
  const [isCompanySpecific, setIsCompanySpecific] = useState(false);
  const [portfolioCompanyId, setPortfolioCompanyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [similarMetrics, setSimilarMetrics] = useState<any[]>([]);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [suggestedDescription, setSuggestedDescription] = useState<string | null>(null);

  const { data: currentUser } = useCurrentUser();
  const { data: portfolioCompanies } = usePortfolioCompanies(fundId);
  const createDefinition = useCreateMetricDefinition();
  const updateDefinition = useUpdateMetricDefinition();

  const isEdit = !!definition;

  useEffect(() => {
    if (definition) {
      setName(definition.name);
      setDescription(definition.description || '');
      setDataType(definition.data_type as any);
      setIsRequired(definition.is_required);
      setDisplayOrder(definition.display_order);
      setIsCompanySpecific(!!definition.portfolio_company_id);
      setPortfolioCompanyId(definition.portfolio_company_id || null);
    } else {
      setName('');
      setDescription('');
      setDataType('number');
      setIsRequired(false);
      setDisplayOrder(0);
      setIsCompanySpecific(false);
      setPortfolioCompanyId(null);
    }
    setError(null);
    setSimilarMetrics([]);
    setSuggestedDescription(null);
  }, [definition, open]);

  // Debounced duplicate check
  const checkForDuplicates = useCallback(
    async (metricName: string, metricDescription: string) => {
      if (!metricName.trim() || isEdit) return;

      setCheckingDuplicates(true);
      try {
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-metric-similarity`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            fundId,
            metricName,
            description: metricDescription || null,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          setSimilarMetrics(data.similar_metrics || []);
          setSuggestedDescription(data.suggested_description || null);
        }
      } catch (err) {
        console.error('Failed to check for duplicates:', err);
      } finally {
        setCheckingDuplicates(false);
      }
    },
    [fundId, isEdit]
  );

  // Debounce the duplicate check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (name.trim()) {
        checkForDuplicates(name, description);
      } else {
        setSimilarMetrics([]);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [name, description, checkForDuplicates]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim() || !currentUser) return;

    try {
      if (isEdit && definition) {
        await updateDefinition.mutateAsync({
          definitionId: definition.id,
          updates: {
            name: name.trim(),
            description: description.trim() || null,
            data_type: dataType,
            is_required: isRequired,
            display_order: displayOrder,
            portfolio_company_id: isCompanySpecific ? portfolioCompanyId : null,
            updated_by: currentUser.id,
          },
        });
      } else {
        await createDefinition.mutateAsync({
          tenant_id: tenantId,
          fund_id: fundId,
          portfolio_company_id: isCompanySpecific ? portfolioCompanyId : null,
          name: name.trim(),
          description: description.trim() || null,
          data_type: dataType,
          is_required: isRequired,
          display_order: displayOrder,
        });
      }

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save metric definition');
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 600, md: 800 } },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6">{isEdit ? 'Edit Metric Definition' : 'Add Metric Definition'}</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              size="small"
              variant="contained"
              onClick={handleSubmit}
              disabled={createDefinition.isPending || updateDefinition.isPending || !name.trim()}
              sx={{ fontSize: '0.75rem', textTransform: 'none' }}
            >
              {createDefinition.isPending || updateDefinition.isPending ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
            <IconButton onClick={handleClose} size="small">
              <X size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {error && <Alert severity="error">{error}</Alert>}

            {/* Similar Metrics Warning */}
            {similarMetrics.length > 0 && (
              <Alert severity="warning" icon={<AlertTriangle size={20} />}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Similar metrics found:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {similarMetrics.map((metric) => (
                    <li key={metric.id}>
                      <Typography variant="body2">
                        <strong>{metric.name}</strong> ({Math.round(metric.similarity * 100)}% similar)
                        {metric.description && ` - ${metric.description}`}
                      </Typography>
                    </li>
                  ))}
                </Box>
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                  Consider using an existing metric instead of creating a duplicate.
                </Typography>
              </Alert>
            )}

            {checkingDuplicates && (
              <Typography variant="body2" color="text.secondary">
                Checking for similar metrics...
              </Typography>
            )}

            <TextField autoFocus label="Metric Name" fullWidth value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Annual Recurring Revenue" required />

            {/* Suggested Description */}
            {!description && suggestedDescription && !isEdit && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button size="small" variant="outlined" startIcon={<Sparkles size={16} />} onClick={() => setDescription(suggestedDescription)} sx={{ textTransform: 'none' }}>
                  Use suggested description
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  {suggestedDescription}
                </Typography>
              </Box>
            )}

            <Box>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this metric (include synonyms for better extraction)"
              />
              {isEdit && (
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<Sparkles size={16} />}
                    onClick={async () => {
                      if (!name.trim()) return;
                      setCheckingDuplicates(true);
                      try {
                        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-metric-similarity`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                          },
                          body: JSON.stringify({
                            fundId,
                            metricName: name,
                            description: null,
                            metricId: definition?.id, // Exclude self in edit mode
                          }),
                        });
                        if (response.ok) {
                          const data = await response.json();
                          if (data.suggested_description) {
                            setDescription(data.suggested_description);
                          }
                        }
                      } catch (err) {
                        console.error('Failed to generate description:', err);
                      } finally {
                        setCheckingDuplicates(false);
                      }
                    }}
                    disabled={checkingDuplicates || !name.trim()}
                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    {checkingDuplicates ? 'Generating...' : 'Generate Description'}
                  </Button>
                </Box>
              )}
            </Box>

            <Divider />

            <FormControl fullWidth required>
              <InputLabel>Data Type</InputLabel>
              <Select value={dataType} label="Data Type" onChange={(e) => setDataType(e.target.value as any)}>
                <MenuItem value="number">Number</MenuItem>
                <MenuItem value="currency">Currency</MenuItem>
                <MenuItem value="percentage">Percentage</MenuItem>
                <MenuItem value="text">Text</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel control={<Checkbox checked={isRequired} onChange={(e) => setIsRequired(e.target.checked)} />} label="Required metric (must be provided in submissions)" />

            <Divider />

            <FormControlLabel
              control={
                <Checkbox
                  checked={isCompanySpecific}
                  onChange={(e) => {
                    setIsCompanySpecific(e.target.checked);
                    if (!e.target.checked) setPortfolioCompanyId(null);
                  }}
                />
              }
              label="Company-Specific Metric"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1, mb: 2, display: 'block' }}>
              Enable this for metrics that are unique to a specific portfolio company (e.g., API Call Volume for Twilio)
            </Typography>

            {isCompanySpecific && (
              <FormControl fullWidth required>
                <InputLabel>Portfolio Company</InputLabel>
                <Select value={portfolioCompanyId || ''} label="Portfolio Company" onChange={(e) => setPortfolioCompanyId(e.target.value)}>
                  {portfolioCompanies?.map((pc) => (
                    <MenuItem key={pc.id} value={pc.id}>
                      {pc.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
